/**
 * The "account locked" message: the user's account is locked after too many invalid sign-in
 * attempts, with a link to `/contact-us`. The copy is `CONSTANTS[language].LOCKOUT_LABEL1`,
 * `CONTACT_US` and `LOCKOUT_LABEL2` from `@ryerson/frontend.common`.
 *
 * Shown for the sign-in error text `LoginErrorResponse.ContactSupport` (`enums/common.enums.ts`),
 * by `components/Login/LoginHelpers/Login.Components.tsx` → `ErrorMessage` on `/login` and by
 * `components/ForgotPassword/ResetPassword/ResetPassword.tsx` for the sign-in that follows a
 * password reset. Both callers take that text from the rejected sign-in's `error.data`, which is
 * undefined for the errors the shared `login` rejects with (see
 * `components/Login/LoginHelpers/LoginForm.tsx` → `onLoginClick`). The counterpart for a required
 * reset is `ResetErrorMessage.tsx`.
 */
import { Icon } from "@ryerson/frontend.assets";
import { CONSTANTS } from "@ryerson/frontend.common";
import { Flex } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Link } from "@ryerson/frontend.navigation";

/**
 * Renders the lock-out message. Reads `window.location.origin` during render, so it runs in the
 * browser only.
 */
const LockOutErrorMessage: React.FC = () => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	return (
		<Flex css={{ marginTop: "20px" }} alignItems="center">
			<Icon
				icon="exclamation"
				color={theme.colors.primary.accentBrand}
				circled={true}
				css={{
					marginRight: "8px",
					width: "34px",
				}}
			/>
			<Typography variant="span" size="sm" color={theme.colors.primary.secondaryBrand}>
				{`${CONSTANTS[language].LOCKOUT_LABEL1} `}
				<Link to={`${window.location.origin}/contact-us`}>
					{CONSTANTS[language].CONTACT_US}
				</Link>
				{CONSTANTS[language].LOCKOUT_LABEL2}
			</Typography>
		</Flex>
	);
};

export default LockOutErrorMessage;
